import React, { useCallback, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {
    ModalNext,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Button
} from '@jutro/components';
import { AsyncButtonLink } from '@jutro/router';
import { ViewModelForm, useDataRefresh } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';

// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

import styles from './FNOLPolicySearchModal.module.scss';
import metadata from './FNOLPolicySearchModal.metadata.json5';
import messages from './FNOLPolicySearchModal.messages';

function FNOLPolicySearchModal(props) {
    const { isComponentValid, onValidate, registerComponentValidation } = useValidation('FNOLSelectPolicyModal');
    const { claimsFilterLOBOptions } = appConfig;
    const {
        isOpen,
        onReject,
        onResolve,
        searchPolicyBySearchCriteria,
        policySearchCriteriaVM,
        handlePolicyCriteriaVMChange
    } = props;

    const { refreshData } = useDataRefresh();

    const validateSearchCriteria = useCallback(() => {
        const policySearchCriteriaValue = _.get(policySearchCriteriaVM, 'value');
        const searchCriteriaInModal = _.omit(policySearchCriteriaValue, ['lossDate', 'country', 'policyNumber']);
        return !_.isEmpty(searchCriteriaInModal);
    }, [policySearchCriteriaVM]);

    useEffect(() => registerComponentValidation(validateSearchCriteria), [
        registerComponentValidation, validateSearchCriteria
    ]);

    const handleClose = useCallback(() => {
        onReject();
    }, [onReject]);

    async function handlePolicySearch() {
        await searchPolicyBySearchCriteria(policySearchCriteriaVM);
        return onResolve(policySearchCriteriaVM);
    }

    const handleChange = useCallback((newModel) => {
        handlePolicyCriteriaVMChange(newModel);
        refreshData();
    }, [handlePolicyCriteriaVMChange, refreshData]);

    const getPolicyTypeForFnol = useCallback(() => {
        const filteredLobOptions = claimsFilterLOBOptions
            .map((value) => {
                return value.replace(/^./, value[0].toUpperCase());
            });
        const policytypes = _.get(policySearchCriteriaVM, 'policyType');
        const filteredPolicyList = [];
        policytypes.aspects.availableValues.forEach((policyTypeObj) => {
            if (filteredLobOptions.includes(policyTypeObj.code)) {
                return filteredPolicyList.push({
                    code: policyTypeObj.code,
                    name: policyTypeObj.code
                });
            }
            return filteredPolicyList;
        });
        return filteredPolicyList;
    }, []);

    const overrides = {
        policyType: {
            availableValues: getPolicyTypeForFnol()
        }
    };

    return (
        <ModalNext
            className={styles.fnolPolicySearchModal}
            isOpen={isOpen}
            onRequestClose={handleClose}
        >
            <ModalHeader
                title={messages.searchForYourPolicy}
                status="info"
                onClose={handleClose}
            />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent}
                    model={policySearchCriteriaVM}
                    overrideProps={overrides}
                    onModelChange={handleChange}
                    onValidationChange={onValidate}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    className={styles.footerButton}
                    id="cancelSearchButton"
                    type="tertiary"
                    onClick={onResolve}
                >
                    {messages.cancelPolicySearch}
                </Button>
                <AsyncButtonLink
                    className={styles.footerButton}
                    id="policySearchButton"
                    disabled={!isComponentValid}
                    onTrigger={handlePolicySearch}
                >
                    {messages.searchPolicy}
                </AsyncButtonLink>
            </ModalFooter>
        </ModalNext>
    );
}

FNOLPolicySearchModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onReject: PropTypes.func.isRequired,
    onResolve: PropTypes.func.isRequired,
    searchPolicyBySearchCriteria: PropTypes.func.isRequired,
    policySearchCriteriaVM: PropTypes.shape({
        policyType: PropTypes.shape({})
    }).isRequired,
    handlePolicyCriteriaVMChange: PropTypes.func.isRequired,
};

export default FNOLPolicySearchModal;
